import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Box, CircularProgress } from '@material-ui/core';
import navigation from '../../hooks/useNavigate';
import { useCreateAccount } from '../../hooks/useAccount';
import { createAccountSchema } from '../../pages/AccountsPage/formik/Schema';
import STRING_KEYS from '../../language/keys';
import { generateSubdomain } from '../../utils';
import { ErrorsStack } from '../ErrorsStack';
import { AccountLinkedinContainer } from '../AddAccountLinkedinDialog/AccountLinkedinContainer';
import { AccountRequest, AssociatedCompany } from '../../@types/Account';
import { CreateNewAccountContent } from './CreateNewAccountContent';
import { style } from './style';

type AddAssociatedAccountDialogProps = {
  isShowDialog: boolean,
  associatedCompany: AssociatedCompany | undefined,
  linkedinUrl: string,
  onCancelClick: () => void,
  onChangeCompanyClicked: () => void,
}

export const AddAssociatedAccountDialog = ({
  isShowDialog,
  associatedCompany,
  linkedinUrl,
  onCancelClick,
  onChangeCompanyClicked,
}:AddAssociatedAccountDialogProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const { goToAccounts } = navigation();
  const actionBtnObj = {
    textBtn: translate(STRING_KEYS.CREATE_ACCOUNT_BTN),
    color: 'secondary',
  } as const;

  const createNewAccount = useCreateAccount(() => {
    goToAccounts();
  });
  const formik = useFormik<AccountRequest>({
    enableReinitialize: true,
    validationSchema: createAccountSchema(),
    initialValues: {
      displayName: associatedCompany?.name ?? '',
      allowedEmailDomain: '', // todo: default value from server
      subDomainName: generateSubdomain(associatedCompany?.name ?? ''),
      logo: associatedCompany?.logo || '',
      companyInfo: {
        id: associatedCompany?.id ?? '',
        name: associatedCompany?.name ?? '',
        city: associatedCompany?.location?.city ?? '',
        state: associatedCompany?.location?.state ?? '',
        country: associatedCompany?.location?.country ?? '',
        size: 'SMALL',
      },
      accountStatus: 'ACTIVE',
      maximumOpenPositions: 0,
      recruitingUsageType: undefined,
    },
    onSubmit: (values: AccountRequest) => {
      createNewAccount.mutateAsync(values);
    },
  });

  const subDomainName = {
    error: Boolean(formik.touched.subDomainName && formik.errors.subDomainName),
    helperText: formik.touched.subDomainName && formik.errors.subDomainName,
    value: formik.values.subDomainName,
  };

  const displayName =  {
    error: Boolean(formik.touched.displayName && formik.errors.displayName),
    helperText: formik.touched.displayName && formik.errors.displayName,
    value: formik.values.displayName,
  };
  const allowedEmailDomainName =  {
    error: Boolean(formik.touched.allowedEmailDomain && formik.errors.allowedEmailDomain),
    helperText: formik.touched.allowedEmailDomain && formik.errors.allowedEmailDomain,
    value: formik.values.allowedEmailDomain,
  };

  const handleValuesChange = (name: string, value: string) => {
    formik.setFieldValue(name, value);
  };

  const error = createNewAccount.isError && createNewAccount.error as AxiosError;

  const renderBodyContent = () => {
    if (createNewAccount.isLoading) {
      return (
        <Box sx={ style.dialog.circular }>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <CreateNewAccountContent
        associatedCompany={ associatedCompany }
        linkedinUrl={ linkedinUrl }
        subDomainName={ subDomainName }
        displayName={ displayName }
        allowedEmailDomain={ allowedEmailDomainName }
        onValuesChange={ handleValuesChange }
        onChangeCompanyClicked={ onChangeCompanyClicked }
      />
    );
  };

  return (
    <form onSubmit={ formik.submitForm }>
      <AccountLinkedinContainer
        titleHeader={ translate(STRING_KEYS.CREATE_NEW_ACCOUNT) }
        onCloseClick={ onCancelClick }
        onActionClick={ formik.submitForm }
        isShowDialog={ isShowDialog }
        color={ actionBtnObj.color }
        textBtn={ actionBtnObj.textBtn }
      >
        <>
          {error && (
            <ErrorsStack
              errors={ error?.response?.data.fieldsErrors }
              errorMessage={ error?.response?.data?.message ||
                translate(STRING_KEYS.ERROR_MESSAGE_CREATE_ACCOUNT) }
            />
          )}
          {renderBodyContent()}
        </>
      </AccountLinkedinContainer>
    </form>
  );
};
